import { ReactNode } from 'react'
import { DialogContentText, Stack } from '@mui/material'
import { MailOutline } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useTranslation } from 'react-i18next'
import Form, { FormProps } from '../../Form'
import PasswordField from '../../PasswordField'
import AccountCard from '../AccountCard'
import {
  SectionTitle,
  StepActions,
  StepContent,
  TextActionButton,
} from '../ReauthenticationDialog.style'
import {
  PasswordFormFields,
  PasswordFormValues,
} from '../ReauthenticationDialog.utils'

interface PasswordStepProps {
  formProps: FormProps<PasswordFormValues>
  email: string
  message: ReactNode
  notices: ReactNode
  closeButton: ReactNode
  isBusy: boolean
  isSendingLink: boolean
  onSendLink: () => void
}

const PasswordStep = ({
  formProps,
  email,
  message,
  notices,
  closeButton,
  isBusy,
  isSendingLink,
  onSendLink,
}: PasswordStepProps) => {
  const { t } = useTranslation()

  return (
    <Form {...formProps}>
      <StepContent>
        <Stack spacing={2.5}>
          <DialogContentText variant="body2" color="text.secondary">
            {message}
          </DialogContentText>
          {notices}
          <div>
            <SectionTitle variant="h3">
              {t('reauthentication.credentials')}
            </SectionTitle>
            <Stack spacing={2}>
              <AccountCard />
              {/* Tells password managers which account the password is for */}
              <input hidden readOnly autoComplete="username" value={email} />
              <PasswordField
                required
                autoFocus
                name={PasswordFormFields.Password}
                label={t('reauthentication.password')}
                autoComplete="current-password"
                disabled={isBusy || isSendingLink}
              />
              {/* For accounts without a password */}
              <TextActionButton
                variant="text"
                fullWidth={false}
                startIcon={<MailOutline />}
                loadingPosition="start"
                loading={isSendingLink}
                disabled={isBusy}
                onClick={onSendLink}
              >
                {t('reauthentication.signInWithLink')}
              </TextActionButton>
            </Stack>
          </div>
        </Stack>
      </StepContent>
      <StepActions disableSpacing>
        {closeButton}
        <LoadingButton
          type="submit"
          variant="contained"
          loading={isBusy}
          disabled={isSendingLink}
        >
          {t('reauthentication.submit')}
        </LoadingButton>
      </StepActions>
    </Form>
  )
}

export default PasswordStep
